import React, { useEffect, useState } from 'react'
import {
  ArrowDown,
  ArrowUpRight,
  BookOpen,
  Building2,
  ChevronRight,
  Compass,
  FlaskConical,
  GraduationCap,
  Layers3,
  Menu,
  Network,
  ShieldCheck,
  Sparkles,
  X,
} from 'lucide-react'
import { SiteSystems } from './site-systems.jsx'

const links = {
  company: 'https://chatakeinnoworks.com/',
  internship: 'https://internship.chatakeinnoworks.com/',
  gfis: 'https://gfis.energy/',
  evidence: 'https://gfis.energy/evidence-portal/',
  press: 'https://innoworkspress.org/',
  journal: 'https://ijaie.org/',
}

const navItems = [
  ['Services', '#services'],
  ['Mandate', '#mandate'],
  ['Learning', '#learning'],
  ['Research', '#research'],
  ['Knowledge', '#knowledge'],
  ['Governance', '#governance'],
]

const pillars = [
  {
    number: '01',
    icon: GraduationCap,
    title: 'Industry Learning',
    text: 'Mentor-led technical practice shaped around real tools, evidence, review, and responsible execution.',
    meta: 'MindforgeAI · flagship programme',
    href: '#learning',
  },
  {
    number: '02',
    icon: FlaskConical,
    title: 'Applied Research',
    text: 'Research programmes that make questions, methods, data provenance, evaluation, and corrections visible.',
    meta: 'GFIS · Apollo · emerging systems',
    href: '#research',
  },
  {
    number: '03',
    icon: BookOpen,
    title: 'Knowledge Systems',
    text: 'Publishing and research communication designed to turn technical work into durable public knowledge.',
    meta: 'Innoworks Press · IJAIE',
    href: '#knowledge',
  },
  {
    number: '04',
    icon: Layers3,
    title: 'Product Practice',
    text: 'A disciplined path from research artefacts and prototypes to tested, useful technology systems.',
    meta: 'Chatake Innoworks · product portfolio',
    href: '#ecosystem',
  },
]

const principles = [
  'Evidence before claims',
  'Practice before credential theatre',
  'Governance before scale',
  'Useful technology before noise',
]

function Mark({ compact = false }) {
  return (
    <span className={`mark ${compact ? 'mark--compact' : ''}`} aria-hidden="true">
      <span className="mark__corner" />
      <span className="mark__rise" />
    </span>
  )
}

function ExternalAnchor({ href, children, className = '' }) {
  return (
    <a className={className} href={href} target="_blank" rel="noreferrer">
      {children}
      <ArrowUpRight size={16} strokeWidth={1.7} aria-hidden="true" />
    </a>
  )
}

export function App() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [menuOpen])

  return (
    <>
      <a className="skip-link" href="#main">Skip to content</a>

      <div className="status-strip" role="note">
        <span className="status-strip__dot" />
        <span>Institute 1.0 · operational institutional gateway</span>
        <span className="status-strip__rule" />
        <span className="status-strip__desktop">Recognition and name-clearance pathways under evaluation</span>
      </div>

      <header className={`site-header ${scrolled ? 'site-header--scrolled' : ''}`}>
        <a className="brand" href="#top" aria-label="CIAT home">
          <Mark compact />
          <span className="brand__type">
            <strong>CIAT</strong>
            <small>Chatake Institute of Advanced Technology</small>
          </span>
        </a>

        <nav className="desktop-nav" aria-label="Primary navigation">
          {navItems.map(([label, href]) => <a key={href} href={href}>{label}</a>)}
        </nav>

        <a className="header-index" href="#ecosystem">
          Institute overview <ChevronRight size={15} aria-hidden="true" />
        </a>

        <button
          className="menu-button"
          type="button"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? <X /> : <Menu />}
        </button>
      </header>

      <div className={`mobile-menu ${menuOpen ? 'mobile-menu--open' : ''}`} aria-hidden={!menuOpen}>
        <nav aria-label="Mobile navigation">
          {navItems.map(([label, href], index) => (
            <a key={href} href={href} onClick={() => setMenuOpen(false)}>
              <span>0{index + 1}</span>{label}
            </a>
          ))}
        </nav>
        <p>Research · Innovation · Industry Learning</p>
      </div>

      <main id="main">
        <section className="hero" id="top">
          <div className="hero__grid" />
          <div className="hero__content shell">
            <div className="hero__main">
              <div className="hero__copy">
                <div className="eyebrow">
                  <Compass size={16} aria-hidden="true" />
                  Institute 1.0 · Company-operated institutional platform
                </div>
                <h1>Advanced technology.<br /><em>Applied with purpose.</em></h1>
                <p className="hero__lead">CIAT connects rigorous technical learning with applied research, publishing, and real product practice—inside one accountable institutional system.</p>
                <div className="hero__actions">
                  <a className="button button--light" href="#mandate">
                    Explore CIAT <ArrowDown size={17} aria-hidden="true" />
                  </a>
                  <ExternalAnchor className="button button--ghost" href={links.company}>
                    Chatake Innoworks
                  </ExternalAnchor>
                </div>
              </div>

              <aside className="hero__panel" aria-label="CIAT institutional model">
                <div className="hero__panel-head">
                  <Mark />
                  <span>CIAT / 1.0</span>
                </div>
                <div className="hero__panel-title">
                  <strong>Chatake Institute of<br />Advanced Technology</strong>
                  <small>An initiative of Chatake Innoworks Private Limited</small>
                </div>
                <div className="hero__panel-grid">
                  {[
                    ['01', 'Industry learning'],
                    ['02', 'Applied research'],
                    ['03', 'Knowledge systems'],
                    ['04', 'Product practice'],
                  ].map(([n, label]) => <div key={n}><span>{n}</span><p>{label}</p></div>)}
                </div>
              </aside>
            </div>

            <div className="hero__foot">
              <div>
                <span>Research</span><i />
                <span>Innovation</span><i />
                <span>Industry Learning</span>
              </div>
              <span className="hero__foot-note">Solapur · India</span>
            </div>
          </div>
        </section>

        <section className="service-gateway" id="services" aria-labelledby="services-title">
          <div className="shell">
            <div className="service-gateway__heading">
              <div><span>Live institutional services</span><h2 id="services-title">One front door.<br />Real working systems.</h2></div>
              <p>CIAT governs the experience while the established Internship Portal continues serving the current cohort during controlled integration.</p>
            </div>
            <div className="service-gateway__grid">
              {[
                ['Student workspace', 'Learning resources, assessments, progress, submissions, fees, and personal project work.', `${links.internship}student.html`, 'Learner service'],
                ['Project workspace', 'Team abstract, daily diaries, tasks, repositories, research notes, and report preparation.', `${links.internship}project-portal.html`, 'Project service'],
                ['Learning library', 'The governed AI/ML learning library, practical resources, notes, and project-phase material.', `${links.internship}aiml-learning-library.html`, 'Knowledge service'],
                ['Admission desk', 'Existing-programme admission records, documents, payment status, receipts, and confirmations.', `${links.internship}admission-desk.html`, 'Current programme'],
              ].map(([title, copy, href, status]) => (
                <ExternalAnchor className="service-card" href={href} key={title}>
                  <span>{status}</span><h3>{title}</h3><p>{copy}</p><strong>Open service</strong>
                </ExternalAnchor>
              ))}
            </div>
            <div className="service-gateway__notice"><ShieldCheck size={20} /><p><strong>Operational boundary:</strong> current services remain issued and operated by Chatake Innoworks Private Limited. This gateway does not create a university, affiliation, accreditation, degree, diploma, or government-recognition claim.</p></div>
          </div>
        </section>

        <section className="manifesto section" id="mandate">
          <div className="shell">
            <div className="section-intro">
              <span className="section-number">01 / Mandate</span>
              <p className="section-lead">CIAT is the company-operated institutional layer connecting learning, research, publishing, products, and industry practice into one accountable system.</p>
            </div>

            <div className="manifesto__grid">
              <div className="manifesto__quote">
                <span className="quote-mark">“</span>
                <h2>Rigor should travel all the way from the classroom to the real world.</h2>
              </div>
              <div className="manifesto__copy">
                <p>We are not building a catalogue of thin courses. Institute 1.0 begins with one flagship learning programme, one observable research programme, and the governance required to make both credible.</p>
                <p>The company remains the legal operator. The institute identity stays deliberately precise about what exists now, what is proposed, and what still requires external recognition.</p>
                <div className="operator-card">
                  <Building2 size={20} aria-hidden="true" />
                  <div>
                    <small>Legal operator</small>
                    <strong>Chatake Innoworks Private Limited</strong>
                    <span>DPIIT-recognised startup · AI & Machine Learning</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="principles">
              {principles.map((item, index) => (
                <div className="principle" key={item}>
                  <span>0{index + 1}</span>
                  <p>{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="pillars section section--ink" id="ecosystem">
          <div className="shell">
            <div className="section-heading section-heading--light">
              <div>
                <span className="section-number">02 / Institutional system</span>
                <h2>One institution.<br />Four connected practices.</h2>
              </div>
              <p>Each pillar has a different job. Together, they form a loop: learn, investigate, communicate, and build.</p>
            </div>

            <div className="pillar-grid">
              {pillars.map(({ number, icon: Icon, title, text, meta, href }) => (
                <a className="pillar-card" href={href} key={title}>
                  <div className="pillar-card__top">
                    <span>{number}</span>
                    <Icon size={24} strokeWidth={1.45} aria-hidden="true" />
                  </div>
                  <div>
                    <h3>{title}</h3>
                    <p>{text}</p>
                  </div>
                  <div className="pillar-card__meta">
                    <span>{meta}</span>
                    <ArrowUpRight size={18} aria-hidden="true" />
                  </div>
                </a>
              ))}
            </div>

            <div className="system-line" aria-label="CIAT operating loop" tabIndex="0">
              <div><span>Learn</span><small>Structured practice</small></div>
              <ChevronRight aria-hidden="true" />
              <div><span>Research</span><small>Observable method</small></div>
              <ChevronRight aria-hidden="true" />
              <div><span>Publish</span><small>Durable knowledge</small></div>
              <ChevronRight aria-hidden="true" />
              <div><span>Build</span><small>Useful systems</small></div>
            </div>
          </div>
        </section>

        <section className="learning section" id="learning">
          <div className="shell">
            <div className="section-heading">
              <div>
                <span className="section-number">03 / Learning</span>
                <h2>Serious technical practice,<br />built around evidence.</h2>
              </div>
              <p>MindforgeAI becomes the programme and lab identity inside CIAT—not a competing institution.</p>
            </div>

            <article className="flagship-card">
              <div className="flagship-card__content">
                <span className="status-badge">Proposed flagship · specification in progress</span>
                <p className="flagship-card__overline">CIAT × MindforgeAI</p>
                <h3>Applied AI & Machine Learning Industry Internship 1.0</h3>
                <p className="flagship-card__summary">A cohort-based, mentor-led experience built around engineering practice, technical review, reproducible project evidence, and responsible use of AI.</p>
                <div className="flagship-card__actions">
                  <ExternalAnchor className="text-link" href={links.internship}>View the existing internship portal</ExternalAnchor>
                  <a className="text-link text-link--muted" href="#governance">Read credential position <ChevronRight size={15} /></a>
                </div>
              </div>

              <div className="flagship-card__framework">
                <p>Programme framework</p>
                {[
                  ['01', 'Foundations', 'Concepts, tools, and technical judgment'],
                  ['02', 'Studio', 'Guided builds and mentor review'],
                  ['03', 'Research practice', 'Methods, evidence, and evaluation'],
                  ['04', 'Capstone', 'A defensible working artefact'],
                ].map(([n, title, copy]) => (
                  <div className="framework-row" key={n}>
                    <span>{n}</span>
                    <div><strong>{title}</strong><small>{copy}</small></div>
                    <ChevronRight size={17} aria-hidden="true" />
                  </div>
                ))}
              </div>
            </article>

            <div className="credential-note">
              <ShieldCheck size={24} strokeWidth={1.5} aria-hidden="true" />
              <p><strong>Credential clarity.</strong> Any pilot credential would be a company-issued Certificate of Completion or Participation through the CIAT initiative—not a degree, diploma, university credit, or government-recognised qualification.</p>
            </div>
          </div>
        </section>

        <section className="research section" id="research">
          <div className="shell">
            <div className="research__header">
              <span className="section-number">04 / Applied research</span>
              <div>
                <h2>Research should be<br />inspectable.</h2>
                <p>Questions, sources, datasets, methods, experiments, failures, provenance, and corrections belong in the record—not behind the claim.</p>
              </div>
            </div>

            <div className="research-grid">
              <article className="research-feature">
                <div className="research-feature__visual" aria-hidden="true">
                  <div className="energy-orbit energy-orbit--one" />
                  <div className="energy-orbit energy-orbit--two" />
                  <div className="energy-core"><Sparkles size={29} strokeWidth={1.3} /></div>
                  <span>GFIS / 001</span>
                </div>
                <div className="research-feature__copy">
                  <div className="research-meta"><span>Candidate anchor programme</span><span>Active product & evidence system</span></div>
                  <h3>Applied AI for Sustainable Systems</h3>
                  <p>GFIS—GreenFuel Intelligence System—is the leading research anchor candidate, connecting machine intelligence, process understanding, renewable-energy operations, and transparent technical evidence.</p>
                  <div className="research-links">
                    <ExternalAnchor className="button button--ink" href={links.gfis}>Explore GFIS</ExternalAnchor>
                    <ExternalAnchor className="button button--line" href={links.evidence}>Open evidence portal</ExternalAnchor>
                  </div>
                </div>
              </article>

              <article className="research-secondary">
                <span className="research-secondary__index">RP / 002</span>
                <div className="research-secondary__icon"><Network size={30} strokeWidth={1.25} /></div>
                <div>
                  <span className="status-badge status-badge--pale">Evidence index in preparation</span>
                  <h3>Apollo</h3>
                  <p>Precision agriculture and climate intelligence. Public programme positioning follows scope, provenance, contributor, and evidence review.</p>
                </div>
              </article>
            </div>

            <div className="method-strip" aria-label="Research method sequence" tabIndex="0">
              {['Question', 'Method', 'Evidence', 'Evaluation', 'Output', 'Correction'].map((item, i) => (
                <React.Fragment key={item}>
                  <span>{item}</span>
                  {i < 5 && <i />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </section>

        <section className="knowledge section section--warm" id="knowledge">
          <div className="shell">
            <div className="section-heading">
              <div>
                <span className="section-number">05 / Knowledge</span>
                <h2>Build the record.<br />Then share it well.</h2>
              </div>
              <p>CIAT’s knowledge layer connects teaching material, technical publishing, and a planned scholarly journal without blurring their roles.</p>
            </div>

            <div className="knowledge-grid">
              <article className="knowledge-card knowledge-card--press">
                <div className="knowledge-card__label"><BookOpen size={18} /> Publishing imprint</div>
                <div>
                  <p className="knowledge-card__monogram">IP</p>
                  <h3>Innoworks Press</h3>
                  <p>Technical books, learning libraries, reports, proceedings, and research communication.</p>
                </div>
                <ExternalAnchor className="knowledge-card__link" href={links.press}>Visit Innoworks Press</ExternalAnchor>
              </article>

              <article className="knowledge-card knowledge-card--journal">
                <div className="knowledge-card__label"><FlaskConical size={18} /> Planned peer-reviewed journal</div>
                <div>
                  <p className="knowledge-card__monogram">IJAIE</p>
                  <h3>Innoworks Journal of Applied Intelligence & Engineering</h3>
                  <p>A planned journal for applied intelligence, engineering systems, and responsible technology research.</p>
                </div>
                <ExternalAnchor className="knowledge-card__link" href={links.journal}>Explore IJAIE</ExternalAnchor>
              </article>
            </div>
          </div>
        </section>

        <section className="network-section section">
          <div className="shell network-section__grid">
            <div className="network-section__intro">
              <span className="section-number">06 / Ecosystem</span>
              <h2>The institutional front door.</h2>
              <p>CIAT doesn’t replace the working ecosystem. It gives every property a clear role, relationship, and path toward a shared quality standard.</p>
            </div>

            <div className="network-map">
              <div className="network-map__centre">
                <Mark compact />
                <strong>CIAT</strong>
                <small>Institutional layer</small>
              </div>
              <ExternalAnchor href={links.company} className="network-node network-node--company"><Building2 /><span>Company</span><small>Operator</small></ExternalAnchor>
              <ExternalAnchor href={links.internship} className="network-node network-node--learning"><GraduationCap /><span>Internship</span><small>Learning</small></ExternalAnchor>
              <ExternalAnchor href={links.gfis} className="network-node network-node--product"><Sparkles /><span>GFIS</span><small>Research product</small></ExternalAnchor>
              <a href="#research" className="network-node network-node--apollo"><Network /><span>Apollo</span><small>Research programme</small></a>
              <ExternalAnchor href={links.press} className="network-node network-node--press"><BookOpen /><span>Press</span><small>Publishing</small></ExternalAnchor>
              <ExternalAnchor href={links.journal} className="network-node network-node--journal"><FlaskConical /><span>IJAIE</span><small>Journal</small></ExternalAnchor>
            </div>
          </div>
        </section>

        <section className="governance section section--ink" id="governance">
          <div className="shell">
            <div className="governance__heading">
              <div>
                <span className="section-number">07 / Governance</span>
                <h2>Credibility is a system,<br />not a slogan.</h2>
              </div>
              <ShieldCheck size={84} strokeWidth={0.75} aria-hidden="true" />
            </div>

            <div className="governance-grid">
              {[
                ['01', 'Identity', 'CIAT is a company-operated initiative of Chatake Innoworks Private Limited. It is not presented as a separately incorporated institution.'],
                ['02', 'Recognition', 'Institutional classification, name clearance, and recognition pathways remain under evaluation before any public institute launch.'],
                ['03', 'Programmes', 'No live CIAT admissions are offered from this preview. Programme claims require an approved specification and learner safeguards.'],
                ['04', 'Credentials', 'No degree, diploma, university credit, professional licence, or government-recognised qualification is claimed.'],
                ['05', 'Research', 'Project claims are admitted only with methods, evidence, contributor roles, provenance, and correction controls.'],
                ['06', 'Privacy', 'Learner accounts, applications, personal data, and certificate records are excluded from this static preview.'],
              ].map(([n, title, copy]) => (
                <article key={n}>
                  <span>{n}</span>
                  <h3>{title}</h3>
                  <p>{copy}</p>
                </article>
              ))}
            </div>

            <div className="governance__footer">
              <p>This private preview is a design and operating-model prototype. It is not an admissions portal, affiliation statement, accreditation claim, or offer of a regulated qualification.</p>
              <ExternalAnchor className="button button--ghost" href={links.company}>Verify the legal operator</ExternalAnchor>
            </div>
          </div>
        </section>

        <section className="closing section">
          <div className="closing__mark" aria-hidden="true"><Mark /></div>
          <div className="shell closing__content">
            <span className="section-number">The working thesis</span>
            <h2>Learn deeply.<br />Research honestly.<br />Build what matters.</h2>
            <p>CIAT — Chatake Institute of Advanced Technology</p>
            <a className="button button--ink" href="#top">Return to the beginning <ArrowUpRight size={16} /></a>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="shell site-footer__top">
          <div className="footer-brand">
            <Mark compact />
            <div><strong>CIAT</strong><span>Research · Innovation · Industry Learning</span></div>
          </div>
          <div className="footer-links">
            <div><small>Institution</small><a href="#mandate">Mandate</a><a href="#learning">Learning</a><a href="#research">Research</a></div>
            <div><small>Ecosystem</small><ExternalAnchor href={links.company}>Company</ExternalAnchor><ExternalAnchor href={links.press}>Press</ExternalAnchor><ExternalAnchor href={links.journal}>Journal</ExternalAnchor></div>
            <div><small>Platforms</small><ExternalAnchor href={links.internship}>Internship</ExternalAnchor><ExternalAnchor href={links.gfis}>GFIS</ExternalAnchor><ExternalAnchor href={links.evidence}>Evidence</ExternalAnchor></div>
          </div>
        </div>
        <div className="shell site-footer__bottom">
          <p>CIAT — Chatake Institute of Advanced Technology, an initiative operated by Chatake Innoworks Private Limited.</p>
          <p>Institute 1.0 · Operational gateway · August 2026</p>
        </div>
      </footer>
      <SiteSystems />
    </>
  )
}
